/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/Components/ui/dialog"

import { FilePlus2 } from "lucide-react";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/Components/ui/tooltip"
import { getProjectChangeRequestColumns, ProjectChangeRequest, columnDisplayNames } from "@/Components/Charter/projectChange_table/columns";
import { DataTableWrapper } from "@/Components/datatable/data-table";
import { useContext, useState } from "react";
import React from "react";
import { AppContext } from "@/App";
import { ColumnFiltersState } from "@tanstack/react-table";
import Confirmation from "@/hooks/confirmation";
import { PageLockContext } from "../PageLockProvider";
import { formatDate } from "date-fns";
import ProjectChangeRequestForm from "./projectChange_table/ProjectChangeRequestForm";
import { DeleteIcon, EditIcon, RefreshIcon } from "@/Components/ui/Icons/TooltipIcon";
import ProjectChangeRequestPopup from "./projectChange_table/ProjectChangeRequestPopup";
import HelpLink from "../ui/help-link";
import { useIsMobile } from "@/hooks/use-mobile";
import { useResponsiveZoom } from "@/hooks/use-responsive-zoom";

export type ProjectChangeRequestColumnVisibility = {
    projectChangeRequestId: boolean
    workstream: boolean
    workPackage: boolean
    description: boolean
    owner: boolean
    date: boolean
    raisedBy: boolean
    decisionMaker: boolean
    dueDate: boolean
    closedDate: boolean
    rating: boolean
    reviewStatus: boolean
    status: boolean
    notes: boolean
    seen: boolean
    acknowledged: boolean
}

export interface ProjectChangeRequestProps {
    passedColumnFilters?: ColumnFiltersState & { workstream?: string, impactRating?: string, status?: string }
    projectChangeTableContent: ProjectChangeRequest[]
    setProjectChangeTableContent: React.Dispatch<React.SetStateAction<ProjectChangeRequest[]>>
}

function Page3_Content({ passedColumnFilters, projectChangeTableContent, setProjectChangeTableContent }: ProjectChangeRequestProps) {
    const app = useContext(AppContext);
    const lock = useContext(PageLockContext);
    const isMobile = useIsMobile();
    const zoomLevel = useResponsiveZoom();

    const useInlineEditing = app.applicationUser?.userSettings?.inlineTableEditing ?? false;

    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [loading, setLoading] = useState<boolean>(true);

    const [dialogTitle, setDialogTitle] = useState("Create New Project Change Request");
    const [dialogTitleBarActions, setDialogTitleBarActions] = useState<JSX.Element | null>(null);
    const [dialogContent, setDialogContent] = useState<JSX.Element | null>(null);

    React.useEffect(() => {
        if (app.authLevel !== 3) { // viewer cant see this page. Sponsor can see it but not edit it
            const down = (e: KeyboardEvent) => {
                if (e.key === "r" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault()
                    addProjectChangeRequest();
                }
            }
            document.addEventListener("keydown", down)
            return () => document.removeEventListener("keydown", down)
        }
    }, [])

    React.useEffect(() => {
        refreshProjectChangeRequests();
    }, [app.selectedProject]);

    const prevProjectChangeTableContentRef = React.useRef<ProjectChangeRequest[]>([]);

    React.useEffect(() => {
        // Skip on initial load or when refreshing all risks
        if (prevProjectChangeTableContentRef.current.length === 0 ||
            projectChangeTableContent.length !== prevProjectChangeTableContentRef.current.length) {
            prevProjectChangeTableContentRef.current = [...projectChangeTableContent];
            return;
        }

        // Find updated risks by comparing with previous state
        const updatedChangeRequest = projectChangeTableContent.filter((currentChangeRequest) => {
            const prevDecision = prevProjectChangeTableContentRef.current.find(p => p.projectChangeRequestId === currentChangeRequest.projectChangeRequestId);
            if (!prevDecision) return false;

            // Compare all properties to detect changes
            return JSON.stringify(prevDecision) !== JSON.stringify(currentChangeRequest);
        });

        // Save each updated risk to the server
        updatedChangeRequest.forEach(changeRequest => {
            if (changeRequest.projectChangeRequestId > 0) { // Only update existing risks, not new ones
                updateProjectChangeRequest(changeRequest);
            }
        });

        // Update ref with current state
        prevProjectChangeTableContentRef.current = [...projectChangeTableContent];
    }, [projectChangeTableContent]);

    function showFormDialog(selectedChangeRequest?: ProjectChangeRequest) {
        const action = selectedChangeRequest ? (
            <div onClick={(e) => {
                e.stopPropagation();
                if (selectedChangeRequest.projectChangeRequestId) {
                    confirmDeleteProjectChangeRequests([selectedChangeRequest.projectChangeRequestId]);
                }
            }}
                className="p-2 rounded-md cursor-pointer hover:bg-red-600">
                <DeleteIcon className={`${isMobile ? "w-8 h-8" : "w-4 h-4"}`} />
            </div>
        ) : null;

        setDialogTitleBarActions(action);

        setDialogTitle(selectedChangeRequest ? "Edit Risk" : "Create New Risk");

        setDialogContent(
            <ProjectChangeRequestForm
                saveProjectChangeRequest={saveProjectChangeRequest}
                projectChangeRequest={selectedChangeRequest}
            />
        );
        setDialogOpen(true);
    }

    function showPopup(selectedChangeRequest: ProjectChangeRequest) {
        const actions = (
            <div className="flex flex-row">
                <div onClick={() => {
                    showFormDialog(selectedChangeRequest);
                }}
                    className="ml-2 p-2 rounded-md cursor-pointer hover:bg-blue-600"
                >
                    <EditIcon className={`${isMobile ? "w-8 h-8" : "w-4 h-4"}`} />
                </div>
                <div onClick={(e) => {
                    e.stopPropagation();
                    if (selectedChangeRequest.projectChangeRequestId) {
                        confirmDeleteProjectChangeRequests([selectedChangeRequest.projectChangeRequestId]);
                    }
                }}
                    className="p-2 rounded-md cursor-pointer hover:bg-red-600">
                    <DeleteIcon className={`${isMobile ? "w-8 h-8" : "w-4 h-4"}`} />
                </div>
            </div>
        );

        setDialogTitle("Change Request Details");
        setDialogTitleBarActions(
            app.authLevel !== 3 && !lock.viewOnly ? actions : null
        );
        setDialogContent(
            <ProjectChangeRequestPopup
                projectChangeRequest={selectedChangeRequest}
            />
        );
        setDialogOpen(true);
    }

    function refreshProjectChangeRequests() {
        if (app.selectedProject) {
            setLoading(true);
            document.body.style.cursor = 'wait';
            fetch(`Projects/ProjectChangeRequests/${app.selectedProject.id}`)
                .then((response) => response.json())
                .then((data) => {
                    setProjectChangeTableContent(data);
                    setLoading(false);
                    document.body.style.cursor = 'default';
                })
                .catch((error) => {
                    app.showActionNotification("Error", "Error fetching project change requests: " + error.message, "destructive");
                    setLoading(false);
                    document.body.style.cursor = 'default';
                });
        }
    }

    function confirmDeleteProjectChangeRequests(projectChangeRequestIds: number[]) {
        setDialogContent(null);
        setDialogTitle(projectChangeRequestIds.length > 1 ? "Are you sure you want to delete these project change requests?" : "Are you sure you want to delete this project change request?");
        setDialogContent(<Confirmation onConfirm={() => { deleteProjectChangeRequests(projectChangeRequestIds); setDialogOpen(false); }} onCancel={() => setDialogOpen(false)} confirmColour="bg-chart-red" />);
        setDialogOpen(true);
    }

    function editChangeRequest(id: number) {
        showFormDialog(projectChangeTableContent.find((request) => request.projectChangeRequestId === id) || undefined);
    }

    function loadColumnVisibility(): ProjectChangeRequestColumnVisibility {
        const localStorageValue = localStorage.getItem(app.applicationUser?.id + "_ProjectChange Requests_columnVisibility");
        if (localStorageValue) {
            const visibilityValues: { columnId: string, isVisible: boolean }[] = JSON.parse(localStorageValue);
            return ({
                projectChangeRequestId: visibilityValues.find(val => val.columnId == "projectChangeRequestId")?.isVisible ?? false,
                workstream: visibilityValues.find(val => val.columnId == "workstream")?.isVisible ?? false,
                workPackage: visibilityValues.find(val => val.columnId == "workPackage")?.isVisible ?? true,
                description: visibilityValues.find(val => val.columnId == "description")?.isVisible ?? true,
                owner: visibilityValues.find(val => val.columnId == "requestedFrom")?.isVisible ?? false,
                date: visibilityValues.find(val => val.columnId == "date")?.isVisible ?? false,
                raisedBy: visibilityValues.find(val => val.columnId == "raisedBy")?.isVisible ?? false,
                decisionMaker: visibilityValues.find(val => val.columnId == "decisionMaker")?.isVisible ?? true,
                dueDate: visibilityValues.find(val => val.columnId == "dueDate")?.isVisible ?? true,
                closedDate: visibilityValues.find(val => val.columnId == "closedDate")?.isVisible ?? false,
                rating: visibilityValues.find(val => val.columnId == "impactRating")?.isVisible ?? true,
                reviewStatus: visibilityValues.find(val => val.columnId == "reviewStatus")?.isVisible ?? true,
                status: visibilityValues.find(val => val.columnId == "status")?.isVisible ?? true,
                notes: visibilityValues.find(val => val.columnId == "notes")?.isVisible ?? false,
                seen: visibilityValues.find(val => val.columnId == "seen")?.isVisible ?? false,
                acknowledged: visibilityValues.find(val => val.columnId == "acknowledged")?.isVisible ?? false,
            });
        }
        return {
            projectChangeRequestId: false,
            workstream: false,
            workPackage: true,
            description: true,
            owner: false,
            date: false,
            raisedBy: false,
            decisionMaker: true,
            dueDate: true,
            closedDate: false,
            rating: true,
            reviewStatus: true,
            status: true,
            notes: false,
            seen: false,
            acknowledged: false,
        }
    }

    function addProjectChangeRequest() {
        if (useInlineEditing) {
            const newProjectChangeRequest: ProjectChangeRequest = {
                projectChangeRequestId: 0,
                projectId: app.selectedProject?.id || 0,
                workstream: app.workPackageOptions?.[0]?.workstreamName || "",
                workPackage: app.workPackageOptions?.[0]?.workPackageNames[0]?.workPackageName || "",
                workstreamId: app.workPackageOptions?.[0]?.workstreamId || 0, // default to first workstream
                workPackageId: app.workPackageOptions?.[0]?.workPackageNames[0]?.workPackageId || 0, // default to first work package
                description: "New Project Change Request",
                owner: app.projectManagers?.[0]?.displayName || "",
                userId: app.projectManagers?.[0]?.id as string,
                date: new Date(),
                raisedBy: "",
                decisionMaker: "",
                dueDate: new Date(),
                closedDate: new Date(),
                rating: "Low",
                reviewStatus: "Pending",
                status: "Open",
                notes: "",
                seen: false,
                acknowledged: false
            };

            saveProjectChangeRequest(newProjectChangeRequest);
        } else {
            showFormDialog();
        }
    }

    function saveProjectChangeRequest(projectChangeRequest: ProjectChangeRequest) {
        if (projectChangeRequest.projectChangeRequestId) {
            updateProjectChangeRequest(projectChangeRequest);
            return;
        }
        document.body.style.cursor = 'wait';
        fetch("Projects/ProjectChangeRequest", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(projectChangeRequest),
        })
            .then((response) => response.json())
            .then((data) => {
                setProjectChangeTableContent((prev) => [data, ...prev]);
                app.showActionNotification("Saved", "Project Change Request Added Successfully!", "success");
                document.body.style.cursor = 'default';
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error adding project change request: " + error.message, "destructive");
                document.body.style.cursor = 'default';
            });
    }

    function updateProjectChangeRequest(projectChangeRequest: ProjectChangeRequest) {
        document.body.style.cursor = 'wait';
        fetch(`Projects/ProjectChangeRequest`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                ...projectChangeRequest,
                date: formatDate(projectChangeRequest.date, "yyyy-MM-dd"),
                closedDate: projectChangeRequest.closedDate ? formatDate(projectChangeRequest.closedDate, "yyyy-MM-dd") : undefined,
                dueDate: projectChangeRequest.dueDate ? formatDate(projectChangeRequest.dueDate, "yyyy-MM-dd") : undefined
            }),
        })
            .then((response) => {
                if (response.ok) {
                    setProjectChangeTableContent((prev) => prev.map((sr) => sr.projectChangeRequestId === projectChangeRequest.projectChangeRequestId ? projectChangeRequest : sr));
                    app.showActionNotification("Success", "Project Change Request Updated Successfully!", "success");
                } else {
                    app.showActionNotification("Error", "Error updating project change request: " + response.statusText, "destructive");
                }
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error updating project change request: " + error.message, "destructive");
                document.body.style.cursor = 'default';
            });
    }

    function deleteProjectChangeRequests(ids: number[]) {
        document.body.style.cursor = 'wait';
        fetch("Projects/ProjectChangeRequests", {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(ids),
        })
            .then(() => {
                setProjectChangeTableContent((prev) => prev.filter((sr) => !ids.includes(sr.projectChangeRequestId)));
                app.showActionNotification("Success", "Project Change Request Deleted", "success");
                document.body.style.cursor = 'default';
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error deleting project change request: " + error.message, "destructive");
                document.body.style.cursor = 'default';
            });
    }

    function setReviewStatus(projectChangeRequestStatus: { id: number, status: "Pending" | "Approved" | "Rejected" }[]) {
        document.body.style.cursor = 'wait';
        fetch("Projects/ProjectChangeRequestReviewStatus", {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(projectChangeRequestStatus),
        })
            .then(() => {
                setProjectChangeTableContent(projectChangeTableContent.map((projectChangeRequest) => {
                    const updatedProjectChangeRequest = projectChangeRequestStatus.find((srs) => srs.id === projectChangeRequest.projectChangeRequestId);
                    if (updatedProjectChangeRequest) {
                        projectChangeRequest.reviewStatus = updatedProjectChangeRequest.status;
                        if (updatedProjectChangeRequest.status === "Rejected") {
                            projectChangeRequest.status = "Closed";
                        }
                    }
                    return projectChangeRequest;
                }));
                app.showActionNotification("Success", "Review Status Updated", "success");
                document.body.style.cursor = 'default';
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error updating review status: " + error.message, "destructive");
                document.body.style.cursor = 'default';
            });
    }

    const groupActions = [
        {
            name: "Delete",
            action: (selectedRows: any[]) => {
                confirmDeleteProjectChangeRequests(selectedRows.map((row) => row.projectChangeRequestId));
            },
        },
        {
            name: "Approve",
            action: (selectedRows: any[]) => {
                const projectChangeRequestStatus = selectedRows.map((row) => ({ "id": row.projectChangeRequestId, "status": "Approved" as "Pending" | "Approved" | "Rejected" }));
                setReviewStatus(projectChangeRequestStatus);
            },
        },
        {
            name: "Reject",
            action: (selectedRows: any[]) => {
                const projectChangeRequestStatus = selectedRows.map((row) => ({ "id": row.projectChangeRequestId, "status": "Rejected" as "Pending" | "Approved" | "Rejected" }));
                setReviewStatus(projectChangeRequestStatus);
            },
        }
    ]

    if (isMobile) {
        return (
            <div className="p-2 m-2">
                <div className="flex flex-row absolute right-2 top-0 -translate-y-[95%]">
                    <div className="text-white p-1 my-3 mx-2 h-min rounded-full hover:bg-blue-600" onClick={() => refreshProjectChangeRequests()}><RefreshIcon className="w-7 h-7" /></div>
                    {(app.authLevel == 4 || app.authLevel == 5) && lock.getLockControl()}
                </div>
                {(app.authLevel == 4 || app.authLevel == 5) && !lock.viewOnly && (
                    <div className="px-2">
                    <button
                        onClick={() => addProjectChangeRequest()}
                        className="w-full text-xl bg-table-header text-table-header-foreground rounded-md p-2"
                    >
                        Add Project Change Request
                    </button>
                    </div>
                )}
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className="flex flex-col w-[95%]" style={{ zoom: zoomLevel }}>
                        <DialogHeader>
                            <DialogTitle><div className="flex"><div className="text-4xl my-auto mr-2">{dialogTitle}</div>{dialogTitleBarActions}</div></DialogTitle>
                        </DialogHeader>
                        {dialogContent}
                    </DialogContent>
                </Dialog>
                <DataTableWrapper
                    columns={getProjectChangeRequestColumns(
                        (ids: number[]) => confirmDeleteProjectChangeRequests(ids),
                        (id: number) => editChangeRequest(id),
                        (reviewStatus: { id: number, status: "Pending" | "Approved" | "Rejected" }[]) => setReviewStatus(reviewStatus),
                    )}
                    data={projectChangeTableContent}
                    setData={setProjectChangeTableContent}
                    loading={loading}
                    viewOnly={app.authLevel === 3 || lock.viewOnly}
                    table_title="ProjectChange Requests"
                    columnVisibility={loadColumnVisibility()}
                    columnDisplayNames={columnDisplayNames}
                    passedColumnFilters={passedColumnFilters as ColumnFiltersState}
                    groupActions={groupActions}
                    onRowClick={!useInlineEditing ? (row) => { showPopup((row.original as ProjectChangeRequest)) } : undefined}
                />
            </div>
        );
    }

    return (
        <div className="p-2 m-2">
            <div className="flex flex-row justify-between">
                <div className="flex flex-row"><h1 className="text-2xl p-2">Project Change Control</h1><div className="text-white p-1 my-3 mx-2 h-min rounded-full hover:bg-blue-600" onClick={() => refreshProjectChangeRequests()}><RefreshIcon className="w-4 h-4" /></div></div>

                <div className="flex flex-col justify-end">
                    <div className="">
                        <div className="flex flex-row ml-auto w-min text-nowrap">
                            {(app.authLevel == 4 || app.authLevel == 5) && lock.getLockControl()}<HelpLink page="charter" />
                        </div>
                    </div>{(app.authLevel == 4 || app.authLevel == 5) && !lock.viewOnly && (
                        <TooltipProvider delayDuration={1}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div
                                        onClick={() => addProjectChangeRequest()}
                                        className="rounded-full bg-table-header text-table-header-foreground w-min p-2 hover:cursor-pointer ml-auto mr-2 mt-4"
                                    >
                                        <FilePlus2 />
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent className="flex flex-col bg-background">
                                    <div className="flex flex-row text-muted-foreground place-content-between">
                                        <span className="p-2">Create Project Change Request</span>
                                    </div>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                </div>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="flex flex-col max-w-fit" style={{ zoom: zoomLevel }}>
                    <DialogHeader>
                        <DialogTitle><div className="flex"><div className="my-auto mr-2">{dialogTitle}</div>{dialogTitleBarActions}</div></DialogTitle>
                    </DialogHeader>
                    {dialogContent}
                </DialogContent>
            </Dialog>
            <DataTableWrapper
                columns={getProjectChangeRequestColumns(
                    (ids: number[]) => confirmDeleteProjectChangeRequests(ids),
                    (id: number) => editChangeRequest(id),
                    (reviewStatus: { id: number, status: "Pending" | "Approved" | "Rejected" }[]) => setReviewStatus(reviewStatus),
                )}
                data={projectChangeTableContent}
                setData={setProjectChangeTableContent}
                loading={loading}
                viewOnly={app.authLevel === 3 || lock.viewOnly}
                table_title="ProjectChange Requests"
                columnVisibility={loadColumnVisibility()}
                columnDisplayNames={columnDisplayNames}
                passedColumnFilters={passedColumnFilters as ColumnFiltersState}
                groupActions={groupActions}
                onRowClick={!useInlineEditing ? (row) => { showPopup((row.original as ProjectChangeRequest)) } : undefined}
            />
        </div>
    );
}

export default Page3_Content;